import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Icon from '../docs/Icon'
import { tokens } from '@/content/system'
import { missions } from '@/content/missions'
import { components } from '@/content/components'
import { contrastPairs } from '@/content/theme'
import { displayFonts, sansFonts } from '@/content/fonts'
import { markStyles, usePrefs } from '@/state/prefs'
import Editable from '../inspector/Editable'
import { textSizeGroup, closingOptions, ctaOptions, displayFontOptions, fontDetail, linkStyleOptions, radiusOptions, sansFontOptions } from './options'
import { useBrand } from '@/state/useBrand'
import Cased from '../ui/Cased'
import { useReducedMotion } from '@/state/useReducedMotion'

function CountUp({ value }: { value: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const reduced = useReducedMotion()
  const [shown, setShown] = useState(reduced ? value : 0)
  useEffect(() => {
    const el = ref.current
    if (!el || reduced) {
      setShown(value)
      return
    }
    let frame = 0
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      const start = performance.now()
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / 1200)
        setShown(Math.round(value * (1 - (1 - t) ** 4)))
        if (t < 1) frame = requestAnimationFrame(tick)
      }
      frame = requestAnimationFrame(tick)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [value, reduced])
  return (
    <span ref={ref} aria-hidden="true">
      {shown.toLocaleString('en-US')}
    </span>
  )
}

export function SystemStats() {
  const stats = [
    { value: missions.length * 2 * contrastPairs.length, label: 'tests pass on every build' },
    { value: missions.length, label: 'mission themes, light and dark' },
    { value: tokens.length, label: 'design tokens' },
    { value: displayFonts.length + sansFonts.length, label: 'open-license typefaces' },
    { value: markStyles.length, label: 'styles for every mark' },
    { value: components.length, label: 'documented components' },
  ]
  return (
    <section className="home-stats" aria-label="The system in numbers">
      <div className="mds-container">
      <div className="home-stats__grid">
        {stats.map((stat) => (
          <div key={stat.label} className="mds-stat">
            <span className="mds-stat__value">
              <CountUp value={stat.value} />
              <span className="mds-visually-hidden">{stat.value}</span>
            </span>
            <span className="mds-stat__label">{stat.label}</span>
          </div>
        ))}
      </div>
      </div>
    </section>
  )
}

export function ClosingBand() {
  const { prefs, set } = usePrefs()
  const { place } = useBrand()
  return (
    <section className="home-closing" aria-labelledby="closing-title">
      <Editable
        label="Section"
        value={prefs.radius}
        options={radiusOptions}
        onChange={(value) => set('radius', value)}
        token="--mds-radius-hero"
        pin="inside-top-right"
        className={`home-closing__canvas home-closing__canvas--${prefs.closing} mds-editable--region`}
        more={[
          {
            label: 'Background',
            value: prefs.closing,
            options: closingOptions,
            onChange: (value) => set('closing', value as typeof prefs.closing),
          },
        ]}
        optionsLabel="Corners"
        attrs={prefs.closing === 'dark' ? { 'data-theme': 'dark', 'data-mission': prefs.mission } : undefined}
      >
        <Editable
          label="Headline font"
          value={prefs.fontDisplay}
          options={displayFontOptions}
          onChange={(value) => set('fontDisplay', value)}
          token="--mds-font-display"
          detail={fontDetail(prefs.fontDisplay)}
          pin="top-right"
        >
          <h2 id="closing-title" className="home-closing__title">
            <Cased>{`Start designing for ${place}.`}</Cased>
          </h2>
        </Editable>
        <Editable
          label="Body font"
          value={prefs.fontSans}
          options={sansFontOptions}
          onChange={(value) => set('fontSans', value)}
          token="--mds-font-sans"
            optionsLabel="Font"
            more={[textSizeGroup(prefs.textSize, (value) => set('textSize', value))]}
          detail={fontDetail(prefs.fontSans, sansFonts)}
          pin="right"
        >
          <p className="home-closing__lead">
            Link four stylesheets, choose a mission and build with components that are accessible from the first
            commit.
          </p>
        </Editable>
        <div className="mds-cluster home-closing__actions">
          <Editable
            label="Call to action"
            value={prefs.cta}
            options={ctaOptions}
            onChange={(value) => set('cta', value)}
            token="mds-button"
            pin="below-center"
            as="span"
          >
            <Link
              to="/getting-started"
              className={`mds-button mds-button--${prefs.cta === 'outline' ? 'secondary' : prefs.cta} mds-button--lg home-closing__cta`}
            >
              <Cased>Get Started</Cased> <Icon name="arrow" size={18} />
            </Link>
          </Editable>
          <Editable
            label="Link style"
            value={prefs.heroLinks}
            options={linkStyleOptions}
            onChange={(value) => set('heroLinks', value)}
            token="mds-button"
            pin="below-center"
            as="span"
          >
            <Link to="/missions" className={`mds-button mds-button--lg ${linkClass(prefs.heroLinks)}`}>
              <Cased>Find your mission</Cased>
            </Link>
          </Editable>
        </div>
      </Editable>
    </section>
  )
}

/** The secondary closing link follows the hero's link style. */
function linkClass(style: string) {
  return style === 'text' ? 'mds-button--ghost' : 'mds-button--secondary'
}
